/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { UserPermission } from '@elio/emanager-front/auth/model';
import { UserSelectors } from '@elio/emanager-front/auth/state';
import { AppConfigService } from '@elio/emanager-front/shared/app-config';
import { BACKEND_URL, BACKEND_URL_KOALA } from '@elio/shared/models';
import { Store } from '@ngrx/store';
import { exhaustMap, Observable, of, take } from 'rxjs';

@Injectable()
export abstract class AbstractService {
  protected apiUrl = BACKEND_URL;
  protected koalaBaseUrl = BACKEND_URL_KOALA;
  protected http = inject(HttpClient);
  protected appConfigService: AppConfigService = inject(AppConfigService);
  protected store = inject(Store);

  httpRequest<T>({ request }: { request: () => Observable<T> }): Observable<T>;

  httpRequest<T>({
    request,
    permission,
  }: {
    request: () => Observable<T>;
    permission: UserPermission | UserPermission[];
  }): Observable<T>;

  httpRequest<T>({
    request,
    permissions,
  }: {
    request: () => Observable<T>;
    permissions: UserPermission[];
  }): Observable<T>;

  httpRequest<T>({
    request,
    permission,
    returnValue,
    throwException,
  }: {
    request: () => Observable<T>;
    permission: UserPermission | UserPermission[];
    throwException: false;
    returnValue: T;
  }): Observable<T>;

  httpRequest<T>({
    request,
    permissions,
    returnValue,
    throwException,
  }: {
    request: () => Observable<T>;
    permissions: UserPermission[];
    throwException: false;
    returnValue: T;
  }): Observable<T>;

  /**
   * @param request http request to be executed
   * @param permission check if user has any permission required to execute the request
   * @param permissions check if user has all the permissions to execute the request.
   * @param throwException if true, the request will throw an exception if the user doesn't have the permission
   * @param returnValue value to be returned if user doesn't have the permission and throwException is false
   */
  httpRequest<T>({
    request,
    permission,
    permissions,
    throwException,
    returnValue,
  }: {
    request: () => Observable<T>;
    permission?: UserPermission | UserPermission[];
    permissions?: UserPermission[];
    throwException?: boolean;
    returnValue?: T;
  }): Observable<T> {
    const executeRequest = (hasPermission: boolean): Observable<T> => {
      if (hasPermission) return request();

      if (throwException)
        throw new Error(`User has not the required permission: ${permission} for http call : ${request}`);

      console.warn(`User has not the required permission: ${permission} for http call : ${request}`);
      return of(returnValue!); // returnValue is mandatory when throwException is false
    };

    if (permission) {
      return this.store.select(UserSelectors.hasAnyPermission(permission)).pipe(take(1), exhaustMap(executeRequest));
    }
    if (permissions) {
      return this.store.select(UserSelectors.hasEveryPermission(permissions)).pipe(take(1), exhaustMap(executeRequest));
    }
    return request();
  }
}
